import { proxy, subscribe } from 'valtio';

import { type MoreActions } from '@canvas/frame-api-types';

import { getBoardIdFromUrlOrEnvironment } from './get-board-id-from-environment';
import { isLocalEAPDevMode } from './is-dev-mode';

const DEVLOOP_KEY = 'canvas-dev-loop';
const CLEAR_DEVLOOP_KEY = 'clear-canvas-dev-loop';

export interface DevelopmentUIState {
  isNavbarCollapsed: boolean;
  contentId?: string;
  moreActions?: MoreActions;
  accountId: string;
  cloudId: string;
  activationId: string;
  containerKey: string;
  containerId: string;
  containerName: string;
  boardId?: string;
  activeDevUiPanel: string | null;
  isAnnouncementSectionCollapsed: boolean;
  seenAnnouncementsHash: string | null;
}

export const currentDevelopmentUIState = proxy<DevelopmentUIState>({
  accountId: process.env.VITE_CANVAS_EAP_ACCOUNT_ID ?? 'no-account-id',
  cloudId: process.env.VITE_CANVAS_EAP_CLOUD_ID ?? 'no-cloud-id',
  activationId:
    process.env.VITE_LOCAL_ACTIVATION_ID ??
    process.env.VITE_CANVAS_EAP_ACTIVATION_ID ??
    'no-activation-id',
  containerKey: process.env.VITE_CANVAS_EAP_CONTAINER_KEY ?? 'no-container-key',
  containerId: process.env.VITE_CANVAS_EAP_CONTAINER_ID ?? 'no-container-id',
  containerName: process.env.VITE_CANVAS_EAP_CONTAINER_NAME ?? 'no-container-name',
  boardId: getBoardIdFromUrlOrEnvironment(),
  contentId: '',
  isNavbarCollapsed: false,
  activeDevUiPanel: null as string | null,
  isAnnouncementSectionCollapsed: false,
  seenAnnouncementsHash: null,
});

function killDevloopValue() {
  /**
   * Keep these console logs in to surface what's happening under the hood
   */
  console.log(`About to delete ${DEVLOOP_KEY} in local storage`);
  localStorage.removeItem(DEVLOOP_KEY);
  return true;
}

export function getStoredDevelopmentState() {
  const storedDevelopmentState = globalThis.localStorage.getItem(DEVLOOP_KEY);
  if (storedDevelopmentState) {
    const stored = JSON.parse(storedDevelopmentState) as typeof currentDevelopmentUIState;
    stored.boardId = getBoardIdFromUrlOrEnvironment(stored.boardId);
    const clearCanvasDevLoop = new URL(window.location.href).searchParams.get(
      CLEAR_DEVLOOP_KEY,
    );

    /**
     * `clear-canvas-dev-loop` has been set and requested,
     *
     * Kill its presence in local storage, and clear out the param to ensure it
     * only runs once
     */
    if (clearCanvasDevLoop === 'delete') {
      const url = new URL(window.location.href);
      killDevloopValue();

      /**
       * Keep these console logs in to surface what's happening under the hood
       */
      console.log(
        `About to clear ${CLEAR_DEVLOOP_KEY} in query param to prevent multiple deletions on live reloads.`,
      );
      url.searchParams.delete(CLEAR_DEVLOOP_KEY);
      try {
        window.history.replaceState(null, '', url);
      } catch (error) {
        console.error('Replace state failed: ', error);
      }
    }

    if (
      // if data is coming from a different site, kill the local storage entry -
      // it is invalid
      process.env.VITE_CANVAS_EAP_CLOUD_ID &&
      stored.cloudId !== process.env.VITE_CANVAS_EAP_CLOUD_ID
    ) {
      killDevloopValue();
      return;
    }

    return stored;
  }
}

export const setupDevelopmentUIState = () => {
  const initialDevelopmentState = getStoredDevelopmentState();

  if (initialDevelopmentState) {
    currentDevelopmentUIState.accountId = initialDevelopmentState.accountId;
    currentDevelopmentUIState.cloudId = initialDevelopmentState.cloudId;
    currentDevelopmentUIState.activationId =
      initialDevelopmentState.activationId ?? currentDevelopmentUIState.activationId;
    currentDevelopmentUIState.containerKey = initialDevelopmentState.containerKey;
    currentDevelopmentUIState.containerId = initialDevelopmentState.containerId;
    currentDevelopmentUIState.containerName = initialDevelopmentState.containerName;
    currentDevelopmentUIState.boardId = initialDevelopmentState.boardId;
    currentDevelopmentUIState.contentId = initialDevelopmentState.contentId;
    currentDevelopmentUIState.isNavbarCollapsed =
      initialDevelopmentState.isNavbarCollapsed ?? false;
    currentDevelopmentUIState.activeDevUiPanel =
      initialDevelopmentState.activeDevUiPanel ?? null;
    currentDevelopmentUIState.moreActions = initialDevelopmentState.moreActions;
    currentDevelopmentUIState.isAnnouncementSectionCollapsed =
      initialDevelopmentState.isAnnouncementSectionCollapsed ?? true;
    currentDevelopmentUIState.seenAnnouncementsHash =
      initialDevelopmentState.seenAnnouncementsHash ?? null;
  }

  subscribe(currentDevelopmentUIState, () => {
    localStorage.setItem(DEVLOOP_KEY, JSON.stringify(currentDevelopmentUIState));
  });
};

export const getLocalBoardUrl = () => {
  const contentId = currentDevelopmentUIState.contentId;
  if (!contentId) {
    return;
  }

  const containerKey = currentDevelopmentUIState.containerKey;
  if (!containerKey) {
    return;
  }

  const site = process.env.VITE_LOCAL_SITE ?? 'https://pug.jira-dev.com';
  const path = globalThis.location?.pathname?.startsWith('/slide/')
    ? 'slide'
    : 'whiteboard';
  return `${site}/wiki/spaces/${containerKey}/${path}/${contentId}`;
};

// Only run in browser environments, not in Node.js (like Canvas server)
if (typeof window !== 'undefined' && isLocalEAPDevMode()) {
  setupDevelopmentUIState();
}
